import { Button, NumberInput, TextInput, Textarea } from "@mantine/core";
import { isInRange, isNotEmpty, useForm }           from "@mantine/form";
import { useRouter }                                from "next/router";
import { useState }                                 from "react";

import { useGetDeviceType }        from "_common/hooks/global";
import toast                       from "_common/toast";
import { useAddQueryParam }        from "_common/utils";
import { PrimaryButton }           from "_components/atoms/button";
import Icon                        from "_components/atoms/icon";
import Image                       from "_components/atoms/image";
import Typography                  from "_components/atoms/typography";
import { initiateDonationPayment } from "_services/api/donate";

import donationFlowPaytm    from "./donationFlowPaytm";
import DonationHistory      from "./donationHistory";
import DonationSuccessModal from "./donationSuccessModal/donationSuccessModal";
import LoadPaytmScript      from "./loadPaytmScript";
import useStyles            from "./styles";

const CustomInput = allProps => {
	const { classNames, ...props } = allProps;
	const { classes, cx }          = useStyles ( {}, { name: "customInput" } );

	if ( props.inputType === "NumberInput" )
		return (
			<NumberInput
				hideControls
				classNames = { {
					input       : cx ( classes.input, classNames?.input ),
					icon        : cx ( classNames?.icon ),
					placeholder : cx ( classes.inputPlaceholder )
				} }
				radius  = "md"
				size    = "md"
				variant = "filled"
				{ ...props }
			/>
		);
	if ( props.inputType === "Textarea" )
		return (
			<Textarea
				autosize
				classNames = { {
					input       : cx ( classes.textInput, classNames?.input ),
					icon        : cx ( classNames?.icon ),
					label       : cx ( classNames?.label ),
					placeholder : cx ( classes.inputPlaceholder )
				} }
				maxLength = { 100 }
				radius    = "md"
				size      = "md"
				variant   = "filled"
				{ ...props }
			/>
		);

	return (
		<TextInput
			classNames = { {
				input       : cx ( classes.input, classNames?.input ),
				icon        : cx ( classNames?.icon ),
				placeholder : cx ( classes.inputPlaceholder )
			} }
			radius  = "md"
			size    = "md"
			variant = "filled"
			{ ...props }
		/>
	);
};

async function onDonate ( { form, sportsFanId, amount, donorDetails, setLoading, onDonate, addQueryParam, router } ) {

	if ( form.validate ().hasErrors )
		return;

	setLoading ( true );

	const initiatePaymentResponse = await initiateDonationPayment ( { amount, sportsFanId, donorDetails } );

	donationFlowPaytm ( {
		moneyDonationId : initiatePaymentResponse.moneyDonationId,
		token           : initiatePaymentResponse.txnToken,
		amount,
		onSuccess       : () => {
			router.push ( { query: { ...router.query,
				moneyDonationId  : initiatePaymentResponse.moneyDonationId,
				moneyDonationKey : initiatePaymentResponse.moneyDonationKey } }
			);
		},
		onFailure: () => {
			toast.error ( "Something went wrong, please try again." );
		},
		onScriptSuccess: () => {
			setLoading ( false );
		},
		onScriptFailure: () => {

			toast.error ( "Something went wrong, please try again." );
			setLoading ( false );
		}

	} );
	setLoading ( false );

}

const Component =  ( { streamerDetails, donationDetails } ) => {
	const deviceType    = useGetDeviceType ();
	const isMobile      = deviceType === "mobile";
	const addQueryParam = useAddQueryParam ();
	const router        = useRouter ();

	const { classes, cx }         = useStyles ( { isMobile }, { name: "donatePage" } );
	const [ loading, setLoading ] = useState ( false );

	const form = useForm ( {
		validateInputOnBlur : false,
		initialValues       : {
			name           : "",
			phone          : "",
			donationAmount : 0,
			message        : ""
		},
		validate: {
			name           : isNotEmpty ( "Name cannot be empty" ),
			phone          : value => ( /^[0-9]*$/.test ( value ) ? null : "Invalid Phone Number" ),
			donationAmount : isInRange ( { min: 10, max: 99999 }, "" )
		}
	} );

	function addDonation ( value ) {
		form.setFieldValue ( "donationAmount", +form.values.donationAmount + value );
	}

	return (
		<div className = { classes.container }>
			<LoadPaytmScript />

			<DonationSuccessModal
				key             = { router.query.moneyDonationId }
				donationDetails = { donationDetails }
				streamerDetails = { streamerDetails }
			/>

			<div className = { classes.mainContainer }>
				<div className = { classes.donateCard }>
					<Typography
						className = { cx ( classes.bold, classes.dimmed ) }
						title     = "Show your support to"
					/>

					<div className = { classes.nameContainer }>
						{ streamerDetails.photo && (
							<Image
								className = { classes.userPhoto }
								src       = { streamerDetails.photo }
								type      = "hdpi"
							/>
						)}

						<Typography
							component = "h1"
							size      = "xxl"
							title     = { streamerDetails.name }
						/>

						{
							streamerDetails.isCeleb ? (
								<Icon
									sameInBothTheme
									name = "verifiedProfile"
									size = { 24 }
								/>
							) : null
						}

					</div>

					<form
						className = { classes.formContainer }
						onSubmit  = { e => e.preventDefault () }
					>
						<CustomInput
							label       = "Name"
							placeholder = "Enter your name"
							{ ...form.getInputProps ( "name" ) }
						/>

						<CustomInput
							classNames  = { { icon: classes.rupeeContainer, input: classes.amountInput } }
							icon        = { <div>+ 91</div> }
							label       = "Phone"
							maxLength = { 12 }
							placeholder = "Phone Number"
							type        = "number"
							{ ...form.getInputProps ( "phone" ) }
						/>

						<div className = { classes.donationBox }>

							<CustomInput
								classNames = { { icon: classes.rupeeContainer, input: classes.amountInput } }
								icon       = { <div className = { classes.rupee }>₹</div> }
								inputType  = "NumberInput"
								label      = "Your Donation"
								max        = { 99999 }
								min        = { 10 }
								type       = "number"
								{ ...form.getInputProps ( "donationAmount", { type: "number" } ) }
							/>

							<Typography dimmed
								size = "sm"
								title = { "Enter an Amount between ₹ 10 - ₹ 99,999" }
							/>

							<div className   = { classes.amountChipsContainer }>
								<Button className   = { classes.amountChip }
									onMouseDown = { () => addDonation ( 10 ) }
								>
									₹ 10
								</Button>

								<Button className = { classes.amountChip }
									onClick   = { () => addDonation ( 100 ) }
								>
									₹ 100
								</Button>

								<Button className = { classes.amountChip }
									onClick   = { () => addDonation ( 1000 ) }
								>
									₹ 1000
								</Button>

							</div>

						</div>

						<CustomInput
							classNames = { { label: classes.msgLabelContainer } }
							inputType  = "Textarea"
							label      = { (
								<>
									<div>
										{`Drop a message for ${ streamerDetails.name }`}
									</div>

									<div className = { classes.wordsLeft }>
										{`${ form.values.message.length } / 100`}
									</div>
								</>
							) }
							minRows = { 3 }
							placeholder = "Say something amazing..."
							{ ...form.getInputProps ( "message", { type: "text" } ) }
						/>

						<PrimaryButton
							className = { classes.donateBtn }
							loading   = { loading }
							onClick   = { () => onDonate ( {
								form,
								amount       : form.values.donationAmount,
								donorDetails : {
									name    : form.values.name,
									mobile  : form.values.phone,
									comment : form.values.message
								},
								sportsFanId: streamerDetails.id,
								setLoading,
								addQueryParam,
								router
							} ) }
							title = "Donate"
						/>

						<div className = { classes.bottomInfo }>
							<Icon
								sameInBothTheme
								name = "infoFilled"
								size = { 18 }
							/>

							<Typography
								className = { classes.dimmed }
								size      = "xs"
								title     = "70% of the Total Donation will be given to Streamer"
							/>
						</div>
					</form>

				</div>

				<DonationHistory streamerId = { streamerDetails.id } />
			</div>
		</div>
	);
};

export default Component;
